import React from 'react';
import { css } from '@emotion/react';
import { BsFillUnlockFill } from 'react-icons/bs';
import { FaGlobeAmericas } from 'react-icons/fa';
import AvatarUserPanel from '../../Common/AvatarUserPanel';
import { MdOutlineAnalytics } from 'react-icons/md';
import { BootstrapToolTip } from '@/_elements/ToolTip';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';

const styles = {
    card: css`
        border-radius: 5px;
        padding: 0px 10px 0px 10px;
        width: 100%;
        color: #000;
    `,
    header: css`
        display: flex;
        align-items: center;
        margin-bottom: 10px;

        @media (max-width: 1200px) {
            flex-direction: column;
            text-align: center;
        }
    `,
    title: css`
        font-size: 1.6rem;
        font-weight: 600;
        margin-left: 10px;

        @media (max-width: 1200px) {
            margin-left: 0;
            margin-top: 8px;
        }
    `,
    contentContainer: css`
        display: flex;
        flex-direction: row;
        justify-content: space-between;

        @media (max-width: 1200px) {
            flex-direction: column;
        }
    `,
    avatarSection: css`
        margin-left: -10px;
        margin-right: 5px;
        display: flex;
        align-items: flex-start;

        @media (max-width: 1200px) {
            margin-left: 0;
            justify-content: center;
            margin-bottom: 15px;
        }
    `,
    infoSection: css`
        flex: 1;

        @media (max-width: 1200px) {
            width: 100%;
        }
    `,
    actionsSection: css`
        display: flex;
        flex-direction: column;
        justify-content: flex-end;

        @media (max-width: 1200px) {
            align-items: center;
            margin-top: 15px;
        }
    `,
    row: css`
        font-size: 1.4rem;
        display: flex;
        flex-direction: row;

        @media (max-width: 480px) {
            flex-direction: column;
            margin-bottom: 8px;
        }
    `,
    rowWithShowMore: css`
        font-size: 1.4rem;
        display: flex;
        flex-direction: row;
    `,
    label: css`
        display: inline-block;
        min-width: 100px;

        @media (max-width: 480px) {
            min-width: auto;
            font-weight: bold;
            margin-bottom: 2px;
        }
    `,
    valueContainer: css`
        align-items: center;
        flex: 1;
    `,
    icon: css`
        margin-left: 5px;
    `,
    dashboardWrapper: css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
        padding: 4px;
        cursor: pointer;
        &:hover {
            box-shadow:
                0 6px 18px rgba(23, 162, 184, 0.25),
                0 0 10px rgba(23, 162, 184, 0.6);
        }

        span {
            display: none;
        }
        @media (max-width: 1200px) {
            span {
                display: inline;
            }
        }
    `,
    dashboardIcon: css`
        color: #17a2b8;
        font-size: 24px;
        display: inline-block;
        vertical-align: middle;
        &:hover {
            transform: scale(1.08);
        }
    `,
    value: css`
        word-break: break-word;
    `,
    showMore: css`
        border: 1px solid #50aeb0;
        border-radius: 5px;
        padding: 3px 6px;
        display: inline-block;
        cursor: pointer;
        font-size: 1.4rem;
        color: #50aeb0;
    `,
    showMoreContainer: css`
        display: flex;
        justify-content: flex-end;
    `,
};

const InfoCard = ({ data, teamInfo }) => {
    return (
        <div css={styles.card}>
            {/* Header Section */}
            <div css={styles.header}>
                {data?.avatar && !teamInfo ? <AvatarUserPanel large={false} item={data.avatar} /> : null}

                {data?.cardTitle && !teamInfo ? (
                    <div css={styles.title}>
                        # {data.cardTitle}
                        {data?.channelType === 'private' && <BsFillUnlockFill size="10" css={styles.icon} />}
                        {data?.channelType === 'public' && <FaGlobeAmericas size="10" css={styles.icon} />}
                    </div>
                ) : null}
            </div>

            <div css={styles.contentContainer}>
                <div css={styles.avatarSection}>
                    {data?.avatar && teamInfo ? (
                        <AvatarUserPanel large={teamInfo ? true : false} item={data.avatar} />
                    ) : null}
                </div>

                <div css={styles.infoSection}>
                    {(data?.elements || [])?.map((item, index) => (
                        <div key={index} css={styles.row}>
                            <strong css={styles.label}>{item.label}:</strong>
                            <div css={styles.valueContainer}>
                                <span css={styles.value}>{item?.value}</span>
                            </div>
                        </div>
                    ))}
                </div>
                {teamInfo ? null : (
                    <div css={styles.actionsSection}>
                        <div css={styles.dashboardWrapper}>
                            <Link to="/analytics" className="d-flex align-items-center">
                                <BootstrapToolTip text="Open Analytics Dashboard">
                                    <MdOutlineAnalytics css={styles.dashboardIcon} />
                                </BootstrapToolTip>
                                <span className="ml-2 text-sm">Analytics Dashboard</span>
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export const ChannelInfoCard = ({ channel, team }) => {
    const channelData = {
        avatar: team[0],
        cardTitle: channel?.title,
        channelType: channel?.type,
        elements: [
            { label: 'Channel Name', value: channel?.title, icon: channel?.type },
            { label: 'Created at', value: new Date(channel?.createdAt).toDateString() },
            { label: 'Team Name', value: team[0]?.name },
        ],
    };
    return <InfoCard data={channelData} />;
};

export const TeamInfoCard = ({ teamData }) => {
    const team = teamData || {};
    return (
        <InfoCard
            teamInfo={true}
            data={{
                avatar: teamData,
                cardTitle: team?.name,
                elements: [
                    { label: 'Team Name', value: team?.name },
                    { label: 'Owner', value: team?.adminData?.name },
                    { label: 'Total Channels', value: team?.noOfRooms },
                    { label: 'Total Members', value: team?.noOfMembers },
                    { label: 'Created On', value: new Date(team?.createdAt).toDateString() },
                    { label: 'Last updated', value: new Date(team?.updatedAt).toDateString() },
                ],
            }}
        />
    );
};
